import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { PauseRequestDto, PauseResultDto, TodayDto } from '@tiffinify/contracts';
import { formatRanges, mealForTime, mealsOf, type Meal } from '@tiffinify/core';
import { BottomNav, LoadError, PageLoader } from '../components/ui';
import { Back, Btn2, Card, Empty, Group, Hero, Ic, Kick, Note, Pair, Row, Screen, Sm, Tile, Title, Top } from '../components/p';
import { CreateButton, MainHeader } from '../components/nav';
import { FlashBanner } from '../lib/flash';
import { endLabel, errorMessage, formatInr, mealName, mealsLabel } from '../lib/format';
import { post } from '../lib/api';
import { useKitchenDay, useMe, useOrders, useRegistrationInbox, useToday } from '../lib/queries';

const longDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.toLocaleDateString('en-IN', { weekday: 'long' })} · ${d.getDate()} ${d.toLocaleDateString('en-IN', { month: 'long' })}`;
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };

/** Every decision waiting for the kitchen, most urgent first: pause requests, sign-ups, plans ending and money due. */
function useNeeds(d: TodayDto | undefined) {
  const signUps = useRegistrationInbox().data?.requests.length ?? 0;
  const pauses = d?.pauseRequests ?? [];
  const people = d?.needsYou ?? [];
  return { pauses, signUps, people, count: pauses.length + (signUps > 0 ? 1 : 0) + people.length };
}

function NeedsRows({ d, limit }: { d: TodayDto; limit?: number }) {
  const qc = useQueryClient();
  const { pauses, signUps, people } = useNeeds(d);
  const [told, setTold] = useState<{ text: string; link: string; label: string } | null>(null);
  const answer = useMutation({
    mutationFn: ({ r, yes }: { r: PauseRequestDto; yes: boolean }) =>
      post<PauseResultDto | { message: { text: string; link: string } }>(`/pause-requests/${r.id}/${yes ? 'accept' : 'decline'}`),
    onSuccess: (res, { r, yes }) => {
      const first = r.name.split(' ')[0];
      setTold({ ...res.message, label: yes ? `Paused ${first}’s days. Send the confirmation on WhatsApp.` : `Declined for ${first}. Send the message on WhatsApp.` });
      void qc.invalidateQueries();
    },
  });

  const rows = [
    ...pauses.map((r) => (
      <Row key={`p${r.id}`} left={<Ic icon="pause" tone="s" />} t={`${r.name} asks to pause`} s={`${formatRanges(r.days)} · ${mealsLabel(r.slot).toLowerCase()}`}
        right={<Pair>
          <Sm label="No" disabled={answer.isPending} onClick={() => answer.mutate({ r, yes: false })} />
          <Sm label="Pause" k disabled={answer.isPending} onClick={() => answer.mutate({ r, yes: true })} />
        </Pair>} />
    )),
    ...(signUps > 0 ? [<Row key="signups" left={<Ic icon="people" tone="s" />} t={`${signUps} ${signUps === 1 ? 'sign-up' : 'sign-ups'} waiting`} s="From your sign-up link" right={<Sm label="Review" to="/registrations" />} />] : []),
    ...d.needsYou.map((n) => {
      const ending = n.reasons.includes('ending');
      const pay = n.reasons.includes('payment');
      return (
        <Row key={n.subscriptionId} left={<Ic icon={ending ? 'refresh' : 'rupee'} tone={ending ? '' : 's'} />} t={n.name}
          s={[ending ? endLabel(n.daysToEnd, n.endDate) : null, pay ? `${formatInr(n.due)} due` : null].filter(Boolean).join(' · ')}
          right={<Sm label="Remind" to={`/remind/${n.subscriptionId}?type=${ending ? 'renewal' : 'payment'}`} ariaLabel={`Remind ${n.name.split(' ')[0]} on WhatsApp`} />} />
      );
    }),
  ];
  const shown = limit ? rows.slice(0, limit) : rows;
  return (
    <>
      {told && <Note icon="chat">{told.label} <a href={told.link} target="_blank" rel="noreferrer" onClick={() => setTold(null)}>Send</a></Note>}
      {shown.length === 0 ? (
        <Card><Empty title="All clear">No plans ending soon and nothing to collect.</Empty></Card>
      ) : <Card>{shown}</Card>}
      {answer.error && <span className="p-error" role="alert">{errorMessage(answer.error)}</span>}
      {!limit && people.length > 1 && <Btn2 label={`Remind all ${people.length}, one after another`} icon="send" to="/remind-all" />}
    </>
  );
}

/** Deliveries for the meal now: how many are marked, one tap into marking them. */
function DeliveriesTile({ date, meal }: { date: string; meal: Meal }) {
  const p = useKitchenDay(date, meal).data?.progress;
  const listed = p ? p.delivered + p.notHome + p.failed + p.pending : 0;
  return <Tile to={`/delivery?meal=${meal}`} icon="bike" tone="g" t="Deliveries" s={p ? `${p.delivered} of ${listed} marked` : 'Counting…'} />;
}

export function Today() {
  const today = useToday();
  const d = today.data;
  const owner = useMe().data?.user.role === 'owner';
  const needs = useNeeds(d);
  const served = d ? mealsOf(d.meals) : [];
  const meal = mealForTime(served) ?? served[0];
  const totalToday = d ? served.reduce((n, m) => n + d.today[m], 0) : 0;
  const totalTomorrow = d ? served.reduce((n, m) => n + d.tomorrow[m], 0) : 0;
  const due = d ? d.needsYou.reduce((s, n) => s + n.due, 0) : 0;
  // One-off orders add to the plans: the hero counts both, the tile opens them.
  const ord = useOrders(undefined).data;
  const fromOrders = (m: Meal | undefined) => (m ? ord?.meals.find((x) => x.meal === m)?.meals ?? 0 : 0);
  const liveOrders = ord ? ord.orders.filter((o) => o.status !== 'cancelled') : [];
  const waiting = liveOrders.filter((o) => o.status === 'waiting').length;

  return (
    <>
      <Screen gap={14}>
        <MainHeader />
        <FlashBanner />
        {today.isPending ? (
          <PageLoader label="Packing today's tiffins" />
        ) : today.error || !d ? (
          <LoadError what="today’s list" onRetry={() => void today.refetch()} />
        ) : (
          <>
            <Title kick={longDay(d.date)} h={greeting()} />

            <Hero to={`/kitchen?meal=${meal ?? ''}`} label="Meals today and tomorrow">
              <div className="p-sec">
                <Kick>{d.isDeliveryDay && meal ? `${mealName(meal)} today` : 'Today'}</Kick>
                <Kick style={{ color: '#B0A79A' }}>{d.activeCount} on a plan</Kick>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span className="p-big">{d.isDeliveryDay && meal ? d.today[meal] + fromOrders(meal) : 0}</span>
                <span className="p-muted" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>{d.isDeliveryDay ? 'meals to send' : 'Not a delivery day'}</span>
                  {d.isDeliveryDay && meal && fromOrders(meal) > 0
                    ? <span>{d.today[meal]} from plans · {fromOrders(meal)} from orders</span>
                    : d.isDeliveryDay && served.length > 1 && <span>{served.map((m) => `${mealName(m)} ${d.today[m] + fromOrders(m)}`).join(' · ')}</span>}
                </span>
              </div>
              <div className="p-facts">
                <div><Kick>{d.tomorrow.isDeliveryDay ? 'Tomorrow' : 'Next'}</Kick><b>{totalTomorrow} meals</b></div>
                {owner
                  ? <div><Kick>Still due</Kick><b>{formatInr(due)}</b></div>
                  : <div><Kick>All today</Kick><b>{totalToday} meals</b></div>}
              </div>
            </Hero>

            <div className="p-sec">
              <h2 className="p-h2">Needs you</h2>
              {needs.count > 2 && <Link to="/needs">All {needs.count}</Link>}
            </div>
            <NeedsRows d={d} limit={2} />

            {d.isDeliveryDay && meal && (
              <div className="p-g2">
                <DeliveriesTile date={d.date} meal={meal} />
                <Tile to="/menus" icon="receipt" tone="s" t="Orders today" s={`${liveOrders.length}${waiting > 0 ? ` · ${waiting} waiting` : ` ${liveOrders.length === 1 ? 'order' : 'orders'}`}`} />
              </div>
            )}
          </>
        )}
      </Screen>
      <CreateButton />
      <BottomNav />
    </>
  );
}

/** Every decision in one list, with Remind all at the end. */
export function NeedsYou() {
  const today = useToday();
  const d = today.data;
  const needs = useNeeds(d);
  return (
    <Screen gap={16}>
      <Top><Back to="/" label="Today" /></Top>
      <Title h="Needs you" sub={needs.count === 0 ? 'Nothing waiting for you.' : `${needs.count} ${needs.count === 1 ? 'thing' : 'things'}, most urgent first.`} />
      {!d ? <PageLoader label="Loading" /> : (
        <Group label="Decide">
          <NeedsRows d={d} />
        </Group>
      )}
      <Note>Saying no to a pause sends a polite WhatsApp message you can edit.</Note>
    </Screen>
  );
}
